import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import {
  CloudUploadOutlined,
  LinkOutlined,
  EyeOutlined,
  BulbOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Bubble, Sender } from '@ant-design/x';
import {
  Button,
  Flex,
  message,
  notification,
  Upload,
  Space,
  Image as AntImage,
  Popover,
} from 'antd';
import {
  fetchConversationId,
  getConversationMessages,
  updateMessageByConversationER,
  validateUserMuted,
  getConversationQuota,
  getConversationApi,
} from '@/services/chat.js';
import CustomBubbleRender from './components/CustomBubbleRender.js';
import { getSignedUrl, uploadToS3 } from '@/services/attachment.js';
import { useDispatch, useSelector } from 'react-redux';
import './index.css';
import HomeSenderView from './components/HomeSenderView.js';
import GuidePage from './components/GuidePage.js';
import { setReportId } from '@/store/modules/reportSlice.js';
import {
  getConversionListThunk,
  updateConversionAttr,
  setLoadingState,
} from '@/store/modules/globalSlice.js';
// import Compressor from 'compressorjs';
import ChatFooter from '@/components/Footer/ChatFooter.js';
import ChatFooterMobile from '@/components/Footer/ChatFooterMobile.js';
import ChatFeedback from '@/components/Footer/ChatFeedbackFooter.js';
import LoginModal from '@/page/Home/components/LoginModal.js';
import { config } from '@/config/index.js';
import { saveMessageToDB, getMessageFromDB, clearMessageFromDB } from '@/utils/indexedDB.js';
import CountWord from '@/components/CountWord/index.js';
import { getConversationIsFeedbackStatusAPI, updateConversationAPI } from '@/services/chat.js';
import { useRequest } from 'ahooks';
import FeedbackModal from '@/page/Home/components/feedbackModal.js';
import { setIsShowFeedback, setIsShowChatFeedbackFooter } from '@/store/modules/chatSlice.js';
import { useTranslation } from 'react-i18next';
import jiantou from '@/assets/layout/jiantou.svg';
import { isOnlyCtaInput } from '@/utils/ctaUtil.js';
import './index.css';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import SortableThumbnail from './components/SortableThumbnail.js';

const MAX_INPUT = 2000;

const NewChat = props => {
  const [previewImage, setPreviewImage] = useState([]);
  const { themeMode } = useSelector(state => state.global);
  const { isFromHome } = props;
  const [messages, setMessages] = useState([]);
  const [userInputValue, setUserInputValue] = useState('');
  const [fileList, setFileList] = React.useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const senderRef = useRef(null);
  const unsavedMessages = useRef(null);
  const attachmentsRef = useRef(null);
  const { isMobile } = useOutletContext();
  const abortControllerRef = useRef(null);
  const { t, i18n } = useTranslation(['chat', 'translation']);

  const dispatch = useDispatch();
  const { conversionList, userProfile } = useSelector(state => state.global);
  const { isLoggedIn } = useSelector(state => state.auth);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const { externalRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const chatListRef = useRef(null);
  const { loadingStates } = useSelector(state => state.global);
  const isCurrentLoading = loadingStates[externalRef] || false;
  const { isShowFeedback, isShowChatFeedbackFooter } = useSelector(state => state.chat);
  const [senderClickable, setSenderClickable] = useState(true);
  const [scrollButton, setScrollButton] = useState(false);
  const iconSrc = jiantou;
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(5);
  const [retryErrorMessageId, setRetryErrorMessageId] = useState(null);
  const [cotComplete, setCotComplete] = useState(false);
  const [cotRenderComplete, setCotRenderComplete] = useState(false);
  const [allowedResultIds, setAllowedResultIds] = useState(new Set());
  const [isDraggingShown, setIsDraggingShown] = useState(true);
  const [MAX_UPLOAD_COUNT, setMAX_UPLOAD_COUNT] = useState(1);
  const [isFeedbackStatusLoading, setIsFeedbackStatusLoading] = useState(Boolean(externalRef));
  const [isLeavingConversation, setIsLeavingConversation] = useState(false);
  const fileListRef = useRef(fileList);
  const multiImageTipShownRef = useRef(false);
  const uploadLimitBatchRef = useRef(null);
  const maxUploadCountRef = useRef(MAX_UPLOAD_COUNT);
  const lastChatRequestRef = useRef(null);

  const cloneChatValue = value => (value == null ? value : JSON.parse(JSON.stringify(value)));
  const cloneFileListForRetry = files => files.map(file => ({ ...file }));

  useEffect(() => {
    if (cotRenderComplete) {
      const resultIds = messages
        .flatMap(item => (Array.isArray(item.content) ? item.content : []))
        .filter(subItem => subItem.type === 'result')
        .map(subItem => subItem.id);
      setAllowedResultIds(new Set(resultIds));
    }
  }, [cotRenderComplete, messages]);

  useEffect(() => {
    lastChatRequestRef.current = null;
    setRetryErrorMessageId(null);
  }, [externalRef]);

  useEffect(() => {
    const next = userProfile?.multiImageTag === 0 ? 1 : 10;

    setMAX_UPLOAD_COUNT(next);
    maxUploadCountRef.current = next;
  }, [userProfile]);

  const { runAsync: getConversationIsFeedbackStatus } = useRequest(
    getConversationIsFeedbackStatusAPI,
    {
      manual: true,
    }
  );

  useEffect(() => {
    document.body.classList.remove('light', 'dark');
    document.body.classList.add(themeMode);
  }, [themeMode]);

  useEffect(() => {
    fileListRef.current = fileList;
    if (fileList.length < 2) return;

    const hasImage = fileList.some(f => f?.originFileObj?.type?.startsWith('image/'));

    const hasMessage = fileList.some(f => f?.originFileObj?.type?.startsWith('message/'));

    if (hasImage && hasMessage) {
      message.error(t('chat:mailImageConflict'));
      setFileList([]);
    }
  }, [fileList]);

  useEffect(() => {
    let isMounted = true;

    const fetchGetIsFeedback = async () => {
      if (!externalRef) {
        setIsFeedbackStatusLoading(false);
        return;
      }

      setIsFeedbackStatusLoading(true);
      try {
        const response = await getConversationIsFeedbackStatus({ externalRef });
        if (!isMounted) return;

        if (0 === response) {
          dispatch(setIsShowChatFeedbackFooter(false));
        } else {
          dispatch(setIsShowChatFeedbackFooter(true));
        }
      } catch (error) {
        if (isMounted) {
          goHome();
        }
      } finally {
        if (isMounted) {
          setIsFeedbackStatusLoading(false);
        }
      }
    };
    fetchGetIsFeedback();

    return () => {
      isMounted = false;
    };
  }, [dispatch, externalRef, getConversationIsFeedbackStatus]);

  useEffect(() => {
    if (!isLoggedIn && (userInputValue || fileList.length > 0)) {
      const data = {
        text: userInputValue,
        fileList: fileList.map(file => ({
          uid: file.uid,
          name: file.name,
          type: file.originFileObj?.type,
          data: file.originFileObj ? file.originFileObj : null,
        })),
      };
      saveMessageToDB(data);
    }
  }, [userInputValue, fileList]);

  useEffect(() => {
    if (isLoggedIn && userProfile) {
      initializeConversation();
    }

    return () => {
      setMessages([]);
      setUserInputValue('');
      setFileList([]);
    };
  }, [externalRef, navigate, isLoggedIn, userProfile]);

  useEffect(() => {
    if (location.pathname === '/') {
      setTimeout(() => {
        onFocus();
      });
    }
  }, [location]);

  useEffect(() => {
    if (externalRef) {
      setIsLeavingConversation(false);
    }
  }, [externalRef]);

  const dragCounter = useRef(0);

  useEffect(() => {
    const handleDragEnter = e => {
      e.preventDefault();
      dragCounter.current++;
      if (dragCounter.current === 1) {
        setIsDragging(true);
      }
    };

    const handleDragOver = e => {
      e.preventDefault();
    };

    const handleDragLeave = e => {
      e.preventDefault();
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
    };
  }, []);

  //File drag and drop
  const handleDrop = e => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    if (files.length + fileListRef.current.length > maxUploadCountRef.current) {
      notification.open({
        message: '',
        description: (
          <div className="notification-tip">
            <BulbOutlined style={{ color: '#faad14', fontSize: 20 }} />
            <span>
              {1 === maxUploadCountRef.current ? t('chat:uploadLimit') : t('chat:uploadLimitPower')}
            </span>
          </div>
        ),
        placement: 'top',
        duration: 3.5,
      });
      return;
    }

    const incoming = Array.from(files);

    const incomingHasImage = incoming.some(f => f.type.startsWith('image/'));
    const incomingImages = incoming.filter(f => f.type.startsWith('image/'));

    if (incomingImages.length > 0) {
      maybeShowMultiImageTip(incomingImages.length);
    }

    const incomingHasMail = incoming.some(
      f => f.name?.toLowerCase().endsWith('.eml') || f.name?.toLowerCase().endsWith('.msg')
    );

    const existingHasImage = fileListRef.current.some(f =>
      f.originFileObj?.type?.startsWith('image/')
    );

    const existingHasMail = fileListRef.current.some(
      f => f.name?.toLowerCase().endsWith('.eml') || f.name?.toLowerCase().endsWith('.msg')
    );

    if (
      (existingHasMail && incomingHasImage) ||
      (existingHasImage && incomingHasMail) ||
      (incomingHasImage && incomingHasMail)
    ) {
      message.error(t('chat:mailImageConflict'));
      return;
    }

    if (
      incomingHasMail &&
      (existingHasMail ||
        incoming.filter(
          f => f.name?.toLowerCase().endsWith('.eml') || f.name?.toLowerCase().endsWith('.msg')
        ).length > 1)
    ) {
      message.error(t('chat:mailOnlyOne'));
      return;
    }

    for (const file of incoming) {
      beforeUpload(file, incoming);
    }
  };

  const maybeShowMultiImageTip = incomingImageCount => {
    if (
      10 === maxUploadCountRef.current &&
      !multiImageTipShownRef.current &&
      fileListRef.current.length + incomingImageCount > 1
    ) {
      notification.open({
        message: '',
        description: (
          <div className="notification-tip">
            <BulbOutlined style={{ color: '#faad14', fontSize: 20 }} />
            <span>{t('chat:MultipleImagesEventsTips')}</span>
          </div>
        ),
        placement: 'top',
        duration: 3.5,
      });

      multiImageTipShownRef.current = true;
    }
  };

  const updateScrollButton = () => {
    const chat = chatListRef.current;
    if (!chat) return;
    const hasScrollbar = chat.scrollHeight > chat.clientHeight;
    const tenVh = window.innerHeight * 0.2;
    const nearBottom = chat.scrollTop + chat.clientHeight >= chat.scrollHeight - tenVh;

    setScrollButton(hasScrollbar && !nearBottom);
  };

  useEffect(() => {
    const chat = chatListRef.current;
    if (!chat) return;
    // 添加ResizeObserver
    const resizeObserver = new ResizeObserver(updateScrollButton);
    resizeObserver.observe(chat);
    // 监听滚动和窗口变化
    chat.addEventListener('scroll', updateScrollButton);
    window.addEventListener('resize', updateScrollButton);

    return () => {
      resizeObserver.unobserve(chat);
      chat.removeEventListener('scroll', updateScrollButton);
      window.removeEventListener('resize', updateScrollButton);
    };
  }, [location]);

  const scrollToBottom = () => {
    const current = chatListRef.current;
    if (current) {
      current.scrollTop = current.scrollHeight;
    }
  };
  const updateLoadingState = (conversationId, isLoading) => {
    dispatch(setLoadingState({ conversationId, isLoading }));
  };

  const handleLoginModalClose = () => {
    setIsLoginModalOpen(false);
  };

  const handleLogin = provider => {
    window.location.href = `${config.API_BASE_URL}/oauth2/authorization/${provider}`;
    setIsLoginModalOpen(false);
  };

  const getBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const beforeUpload = async (file, incomingFileList = []) => {
    const fileName = file.name.toLowerCase();

    const isImage = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type);
    if (isImage) {
      maybeShowMultiImageTip(1);
    }
    const isMail = fileName.endsWith('.eml') || fileName.endsWith('.msg');

    const hasImage = fileList.some(f => f.originFileObj?.type?.startsWith('image/'));
    const hasMail = fileList.some(
      f => f.name?.toLowerCase().endsWith('.eml') || f.name?.toLowerCase().endsWith('.msg')
    );

    if (!isImage && !isMail) {
      message.error(t('chat:fileTypeError'));
      return Upload.LIST_IGNORE;
    }

    if (isMail) {
      if (hasImage) {
        message.error(t('chat:mailImageConflict')); // Email files cannot be uploaded simultaneously with images.
        return Upload.LIST_IGNORE;
      }
      if (hasMail) {
        message.error(t('chat:mailOnlyOne')); // Only one email file can be uploaded
        return Upload.LIST_IGNORE;
      }
    }

    if (isImage && hasMail) {
      message.error(t('chat:mailImageConflict'));
      return Upload.LIST_IGNORE;
    }

    //quantity limit
    const batchFiles = incomingFileList.length > 0 ? incomingFileList : [file];
    const imageIncomingCount = batchFiles.filter(f => f.type?.startsWith('image/')).length;

    const existingImageCount = fileListRef.current.filter(f =>
      f.originFileObj?.type?.startsWith('image/')
    ).length;

    if (10 === MAX_UPLOAD_COUNT && existingImageCount + imageIncomingCount > 1) {
      if (!multiImageTipShownRef.current) {
        notification.open({
          message: '',
          description: (
            <div className="notification-tip">
              <BulbOutlined style={{ color: '#faad14', fontSize: 20 }} />
              <span>{t('chat:MultipleImagesEventsTips')}</span>
            </div>
          ),
          placement: 'top',
          duration: 3.5,
        });

        multiImageTipShownRef.current = true;
      }
    }

    if (existingImageCount + imageIncomingCount > maxUploadCountRef.current) {
      if (uploadLimitBatchRef.current !== incomingFileList) {
        notification.open({
          message: '',
          description: (
            <div className="notification-tip">
              <BulbOutlined style={{ color: '#faad14', fontSize: 20 }} />
              <span>
                {1 === maxUploadCountRef.current
                  ? t('chat:uploadLimit')
                  : t('chat:uploadLimitPower')}
              </span>
            </div>
          ),
          placement: 'top',
          duration: 3.5,
        });
        uploadLimitBatchRef.current = incomingFileList;
      }
      return Upload.LIST_IGNORE;
    }

    if (file.size > 10 * 1024 * 1024) {
      message.error(t('chat:sizeLimit'));
      return Upload.LIST_IGNORE;
    }

    let preview = null;
    if (isImage) {
      preview = await getBase64(file);
    }

    const uid = `${Date.now()}-${Math.random()}`;

    setFileList(prev => [
      ...prev,
      {
        uid,
        name: file.name,
        originFileObj: file,
      },
    ]);

    if (preview) {
      setPreviewImage(prev => [...prev, preview]);
    }

    setTimeout(() => {
      onFocus();
    }, 100);

    return false;
  };

  const handleRemoveFile = file => {
    setFileList(prev => {
      const next = prev.filter(item => item.uid !== file.uid);
      if (next.length <= 1) {
        multiImageTipShownRef.current = false;
      }
      return next;
    });
    setPreviewImage(prev => prev.filter((_, index) => fileList[index]?.uid !== file.uid));
  };

  const sharedAttachmentProps = {
    items: fileList,
    maxCount: MAX_UPLOAD_COUNT,
    accept: 'image/jpeg,image/png,image/webp,image/gif,.eml,.msg',
    beforeUpload,
    multiple: true,
    onRemove: handleRemoveFile,
    showUploadList: false,
    imageProps: {
      height: '100%',
      width: '100%',
      style: {
        borderRadius: '1rem',
      },
      preview: {
        src: previewImage,
        maskClassName: isMobile ? 'ant-image-preview-mask always-visible-mask' : 'uploadImage',
        mask: (
          <Space direction="vertical" align="center" size={8}>
            <EyeOutlined />
            {t('chat:previewMask')}
          </Space>
        ),
      },
    },
  };

  // Ctrl+C img / Ctrl+V img
  const handlePaste = useCallback(
    async e => {
      if (!e.clipboardData || !e.clipboardData.items) return;

      const items = Array.from(e.clipboardData.items);

      // only take file typy
      const fileItems = items.filter(item => item.kind === 'file');
      if (fileItems.length === 0) return;

      const imageFiles = fileItems
        .map(i => i.getAsFile())
        .filter(f => f && f.type.startsWith('image/'));

      if (imageFiles.length > 0) {
        maybeShowMultiImageTip(imageFiles.length);
      }

      let hasHandled = false;

      for (const item of fileItems) {
        const file = item.getAsFile();
        if (!file) continue;

        const fileName = file.name?.toLowerCase() || '';
        const isImage = file.type.startsWith('image/');
        const isMail =
          fileName.endsWith('.eml') ||
          fileName.endsWith('.msg') ||
          file.type === 'message/rfc822' ||
          file.type === 'application/vnd.ms-outlook';

        if (!isImage && !isMail) continue;

        if (!hasHandled) {
          e.preventDefault();
          hasHandled = true;
        }

        let finalFile = file;

        if (isImage && !file.name) {
          const ext = file.type.split('/')[1] || 'png';
          finalFile = new File(
            [file],
            `paste-${Date.now()}-${Math.random().toString(16).slice(2)}.${ext}`,
            { type: file.type }
          );
        }

        const result = await beforeUpload(finalFile, imageFiles);

        if (result === Upload.LIST_IGNORE) {
          if (isMail) break;
          continue;
        }
      }
    },
    [beforeUpload]
  );

  const initializeConversation = async () => {
    if (externalRef && !unsavedMessages.current) {
      const response = await getConversationMessages(externalRef, userProfile.userId);
      if (response.length === 0) {
        goHome();
      } else if (response) {
        response.forEach(item => Object.assign(item, { typing: false }));
        setMessages(response);
      } else {
        setMessages([]);
      }
    } else if (externalRef && unsavedMessages.current) {
      setUserInputValue(unsavedMessages.current.text);
      setFileList(unsavedMessages.current.fileList);
      handleSendMessage(unsavedMessages.current.text);
      unsavedMessages.current = null;
    } else {
      const saved = await getMessageFromDB();
      if (saved) {
        setUserInputValue(saved.text || '');
        const rebuiltFileList =
          saved.fileList?.map(file => ({
            ...file,
            originFileObj: file.data,
          })) || [];
        setFileList(rebuiltFileList);
        clearMessageFromDB();
      }
    }
    //获取用户的限额
    getQuota();
  };

  const uploadFile = async file => {
    const fileResponse = await getSignedUrl(file);
    await uploadToS3(fileResponse.signedPutUrl, file.originFileObj);
    return fileResponse;
  };

  const onFocus = () => {
    senderRef.current?.focus({
      cursor: 'end',
    });
  };

  // const compressImage = file => {
  //   return new Promise((resolve, reject) => {
  //     new Compressor(file, {
  //       quality: 0.9, // 图片质量 70%
  //       maxWidth: 1536, // 限制最大宽度
  //       maxHeight: 1536, // 限制最大高度
  //       // minWidth‌: 768, // 限制最小宽度
  //       // minHeight‌: 768, // 限制最小高度
  //       mimeType: 'image/jpeg', // 统一转换成 JPEG
  //       success (result) {
  //         const compressedFile = new File([result], file.name, {
  //           type: 'image/jpeg',
  //           lastModified: Date.now(),
  //         });
  //         resolve(compressedFile);
  //       },
  //       error (err) {
  //         console.error('Compression error:', err);
  //         reject(err);
  //       },
  //     });
  //   });
  // };
  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      setFileList(prev => {
        const oldIndex = prev.findIndex(i => i.uid === active.id);
        const newIndex = prev.findIndex(i => i.uid === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });

      setPreviewImage(prev => {
        const oldIndex = prev.findIndex((_, i) => fileList[i]?.uid === active.id);
        const newIndex = prev.findIndex((_, i) => fileList[i]?.uid === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    },
    [fileList]
  );

  const headerNode = fileList.length > 0 && (
    <div style={{ padding: '.5rem' }}>
      <Flex align="center" gap="small">
        <AntImage.PreviewGroup>
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={fileList.map(f => f.uid)} strategy={rectSortingStrategy}>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                {fileList.map((file, index) => (
                  <SortableThumbnail
                    key={file.uid}
                    id={file.uid}
                    index={index}
                    src={previewImage[index]}
                    file={file}
                    onRemove={() => handleRemoveFile(file)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </AntImage.PreviewGroup>
      </Flex>
    </div>
  );

  const resetSender = () => {
    setFileList([]);
    setUserInputValue('');
    multiImageTipShownRef.current = false;
  };

  const sendFromHomeAction = async text => {
    if (!senderClickable) return;

    setSenderClickable(false);
    if (!isFromHome) return;

    unsavedMessages.current = {
      text,
      fileList,
    };
    try {
      const res = await fetchConversationId();
      // updateLoadingState(res.externalRef, true);
      dispatch(getConversionListThunk()).then(() => {
        navigate(`/chat/${res.externalRef}`);
      });
    } catch (e) {
      /* empty */
    }
  };

  const updateConversionNameAfterChat = (externalRef, name) => {
    if (name) dispatch(updateConversionAttr({ conversionId: externalRef, data: { name } }));
  };
  const getQuota = async () => {
    const result = await getConversationQuota();
    setCurrent(result.current);
    setTotal(result.total);
  };

  const getImagePixelHash = async file => {
    const arrayBuffer = await file.arrayBuffer();
    return new Promise((resolve, reject) => {
      const blob = new Blob([arrayBuffer], { type: file.type });
      const img = new Image();
      img.onload = async () => {
        // 转 canvas 获取像素
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, img.width, img.height);
        const pixelData = imageData.data; // Uint8ClampedArray

        // 生成 hash
        const buffer = await crypto.subtle.digest('SHA-256', pixelData);
        const hashArray = Array.from(new Uint8Array(buffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        resolve(hashHex);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(blob);
    });
  };

  const handleSendMessage = async (text = '', flag = 5) => {
    const isRetry = text === 'retry' && flag === 0;
    const retryRequest = isRetry ? lastChatRequestRef.current : null;
    const hasExplicitText = typeof text === 'string' && text.trim().length > 0;

    if (!isLoggedIn) {
      setIsLoginModalOpen(true);
      return;
    }

    if (isRetry && !retryRequest) {
      message.warning(t('chat:retryUnavailable'));
      return;
    }

    if (isRetry) {
      setRetryErrorMessageId(null);
    }

    const retryUsesLocalAttachments =
      isRetry && Array.isArray(retryRequest.fileList) && retryRequest.fileList.length > 0;
    const retryUsesCompletedPayload = isRetry && !retryUsesLocalAttachments;

    if (flag === 5) {
      if (!hasExplicitText && !senderAvailable()) return;
    }

    const effectiveText = isRetry ? retryRequest.payload.message : text;
    const shouldCreateUserMessage = isRetry ? retryRequest.createUserMessage : flag === 5;
    const sendFileList = retryUsesLocalAttachments ? retryRequest.fileList : fileList;
    const sendPreviewImage = retryUsesLocalAttachments
      ? retryRequest.previewImage || []
      : previewImage;
    const shouldUploadAttachments =
      !retryUsesCompletedPayload && Array.isArray(sendFileList) && sendFileList.length > 0;

    try {
      if (flag === 5) {
        const fileLength = fileList.length;
        await validateUserMuted({ message: effectiveText, fileLength: fileLength });
      }
    } catch (error) {
      return;
    }

    if (isFromHome) {
      await sendFromHomeAction(text);
      return;
    }

    // 创建临时上传中消息
    const tempMessageId = Date.now().toString();
    const tempMessage = {
      id: tempMessageId,
      content: { type: 'uploading', text: 'Uploading file...' },
      role: 'user',
      placement: 'end',
      createdAt: new Date(),
      typing: false,
    };

    // 创建临时上传中消息
    if (shouldCreateUserMessage) {
      setMessages(prevMessages => [...prevMessages, tempMessage]);
      if (isOnlyCtaInput(effectiveText)) {
        notification.open({
          message: null,
          description: (
            <div className="notification-tip">
              <BulbOutlined style={{ color: '#faad14', fontSize: 20 }} />
              <span>{t('chat:insufficientNotice')}</span>
            </div>
          ),
          placement: 'top',
          duration: 5,
        });
      }
    }

    const imageExternalRefs = [];
    const signedGetUrls = [];
    const previewUrls = [];
    let uploadCompleted = !shouldUploadAttachments;
    let shouldClearPreviewImage = false;
    let sseReceivedResult = false;
    let streamMsgId = null;

    try {
      const payload = retryUsesCompletedPayload
        ? cloneChatValue(retryRequest.payload)
        : {
            message: effectiveText,
            conversationExternalRef: externalRef,
          };

      if (!isRetry) {
        if (flag === 0) {
          const reEvaluateType = 1;
          Object.assign(payload, { reEvaluateType });
        } else if (flag === 2) {
          const reEvaluateType = 2;
          Object.assign(payload, { reEvaluateType });
        }

        lastChatRequestRef.current = {
          payload: cloneChatValue(payload),
          fileList: cloneFileListForRetry(sendFileList),
          previewImage: [...sendPreviewImage],
          createUserMessage: shouldCreateUserMessage,
        };
      }

      if (shouldUploadAttachments) {
        const res = await Promise.all(sendFileList.map(uploadFile));
        res.forEach(item => {
          imageExternalRefs.push(item.externalRef);
          signedGetUrls.push(item.signedGetUrl);
          previewUrls.push(item.signedGetUrl);
        });
        uploadCompleted = true;
      }

      let userMessageContent = isRetry
        ? cloneChatValue(retryRequest.displayContent)
        : {
            type: 'text',
            content: effectiveText,
          };

      if (shouldCreateUserMessage) {
        let isEmlFile = false;
        let file;
        if (shouldUploadAttachments) {
          file = sendFileList[0].originFileObj;
          isEmlFile =
            file.name.toLowerCase().endsWith('.eml') || file.name.toLowerCase().endsWith('.msg');
        }
        if (shouldUploadAttachments) {
          userMessageContent = isEmlFile
            ? {
                type: 'eml',
                fileName: file.name,
                content: {
                  text: effectiveText,
                },
              }
            : {
                type: 'image',
                content: {
                  text: effectiveText,
                  imageUrls: previewUrls,
                },
              };
        }
        // 更新临时消息为实际内容
        const updatedMessage = {
          ...tempMessage,
          content: userMessageContent,
        };
        setMessages(prevMessages =>
          prevMessages.map(msg => (msg.id === tempMessageId ? updatedMessage : msg))
        );
      }

      resetSender();
      shouldClearPreviewImage = true;
      updateLoadingState(externalRef, true);

      if (shouldUploadAttachments) {
        const file = sendFileList[0].originFileObj;
        Object.assign(payload, { imageExternalRefs, signedGetUrls });
        const isEmlFile =
          file.name.toLowerCase().endsWith('.eml') || file.name.toLowerCase().endsWith('.msg');
        if (isEmlFile) {
          Object.assign(payload, { fileType: 'eml', fileName: file.name });
        } else {
          const imgHash = await getImagePixelHash(file);
          Object.assign(payload, { imgHash });
        }
      }

      if (!retryUsesCompletedPayload) {
        lastChatRequestRef.current = {
          payload: cloneChatValue(payload),
          displayContent: cloneChatValue(userMessageContent),
          createUserMessage: shouldCreateUserMessage,
        };
      }

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      setCotComplete(false);
      setCotRenderComplete(false);
      const requestPayload = {
        ...payload,
        message: btoa(unescape(encodeURIComponent(payload.message))),
        reference: 'Web',
      };
      const response = await fetch(`${config.API_BASE_URL}/client/conversation/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          'Scams-App-Language': i18n.language,
        },
        body: JSON.stringify(requestPayload),
        signal,
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Stream request failed');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // 创建空消息用于最终结果
      streamMsgId = `assistant-stream-${Date.now()}`;
      setMessages(prev => [
        ...prev,
        {
          id: streamMsgId,
          role: 'assistant',
          placement: 'start',
          typing: false,
          content: [{ type: 'cot', content: t('chat:chainFirst'), id: `cot-${Date.now()}` }],
          createdAt: new Date(),
        },
      ]);

      let buffer = '';
      let currentEvent = null;
      let sseErrorReason = null;
      // eslint-disable-next-line
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (let rawLine of lines) {
          rawLine = rawLine.trim();
          if (!rawLine || rawLine.startsWith(':connected')) continue;
          if (!rawLine || rawLine.startsWith(':disconnected')) {
            const response = await getConversationApi(externalRef);
            updateConversionNameAfterChat(externalRef, response.name);
          }
          if (!rawLine || rawLine === ':cotComplete') {
            setCotComplete(true);
          }
          if (rawLine.startsWith('event:')) {
            currentEvent = rawLine.slice(6).trim();
            continue;
          }

          if (rawLine.startsWith('data:')) {
            const dataStr = rawLine.slice(5).trim();
            if (!dataStr) continue;

            if (currentEvent === 'error') {
              try {
                const errorData = JSON.parse(dataStr);
                sseErrorReason = errorData.reason || dataStr;
              } catch {
                sseErrorReason = dataStr;
              }
              sseReceivedResult = true;
              break;
            }

            setMessages(prev =>
              prev.map(m => {
                if (m.id !== streamMsgId) return m;
                if (currentEvent === 'cot') {
                  const lastCotIndex = m.content.map(c => c.type).lastIndexOf('cot');
                  if (lastCotIndex >= 0) {
                    m.content[lastCotIndex].content += dataStr;
                  }
                } else if (currentEvent === 'result') {
                  sseReceivedResult = true;
                  let resultData = null;
                  try {
                    resultData = JSON.parse(dataStr);
                    m.content.push({
                      type: 'result',
                      content: resultData.content,
                      id: `result-${Date.now()}`,
                    });
                    m.createdAt = resultData.createdAt;
                  } catch (e) {
                    // eslint-disable-next-line no-restricted-syntax
                    console.log(dataStr);
                  }
                }
                return { ...m };
              })
            );
          }
        }
      }
      if (sseErrorReason) {
        throw new Error(sseErrorReason);
      }
      setRetryErrorMessageId(null);
      lastChatRequestRef.current = null;
    } catch (error) {
      if (error.name !== 'AbortError') {
        const errorMessageId = `assistant-error-${Date.now()}`;
        setRetryErrorMessageId(errorMessageId);
        if (shouldUploadAttachments && !uploadCompleted && shouldCreateUserMessage) {
          setMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === tempMessageId
                ? {
                    ...msg,
                    content: { type: 'error', text: t('chat:status.uploadFailed') },
                  }
                : msg
            )
          );
        }
        const errorMessage = {
          id: errorMessageId,
          content: {
            type: 'text',
            content: t('chat:apiError'),
          },
          role: 'assistant',
          placement: 'start',
          createdAt: new Date(),
        };
        setMessages(prevMessages => [...prevMessages, errorMessage]);
      } else {
        if (shouldCreateUserMessage) {
          setMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === tempMessageId
                ? {
                    ...msg,
                    content: { type: 'error', text: 'Failed to upload image' },
                  }
                : msg
            )
          );
        }
      }
    } finally {
      setSenderClickable(true);
      getQuota();
      if (shouldClearPreviewImage) {
        setPreviewImage([]);
      }

      if (!sseReceivedResult && !isRetry && streamMsgId && externalRef && userProfile?.userId) {
        // 保持 loading 状态，启动轮询兜底
        const maxAttempts = 5;
        const interval = 5000;
        let attempts = 0;

        const stopPoll = () => {
          clearInterval(poll);
          updateLoadingState(externalRef, false);
        };

        const poll = setInterval(() => {
          attempts++;
          getConversationMessages(externalRef, userProfile.userId)
            .then(history => {
              if (history && history.length > 0) {
                const lastAssistant = [...history].reverse().find(m => m.role === 'assistant');
                if (lastAssistant && Array.isArray(lastAssistant.contents)) {
                  const resultItem = lastAssistant.contents.find(c => c.type === 'result');
                  if (resultItem?.content) {
                    stopPoll();
                    getConversationApi(externalRef)
                      .then(resp => {
                        if (resp?.name) updateConversionNameAfterChat(externalRef, resp.name);
                      })
                      .catch(() => {});
                    setMessages(prev =>
                      prev.map(m => {
                        if (m.id !== streamMsgId) return m;
                        return {
                          ...m,
                          content: [
                            ...m.content,
                            {
                              type: 'result',
                              content: resultItem.content,
                              id: `result-fallback-${Date.now()}`,
                            },
                          ],
                          createdAt: lastAssistant.createdAt,
                        };
                      })
                    );
                    return;
                  }
                }
              }
              if (attempts >= maxAttempts) {
                stopPoll();
              }
            })
            .catch(() => {
              if (attempts >= maxAttempts) {
                stopPoll();
              }
            });
        }, interval);
      } else {
        updateLoadingState(externalRef, false);
      }
    }
  };

  const onCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // 取消请求
      notification.error({
        message: t('chat:cancel.title'),
        description: t('chat:cancel.description'),
      });
    }
    updateLoadingState(externalRef, false);
    updateMessageByConversationER({
      externalRef,
      role: 'user',
      content: { message: t('chat:cancel.updateMsg') },
    });
  };

  const getCurrentConversion = () => {
    return conversionList.find(item => item.externalRef === externalRef);
  };

  const senderAvailable = () => {
    return userInputValue.trim() || (Array.isArray(fileList) && fileList.length);
  };

  const onlySendImage = () => {
    // 输入为空 并且有附件时，直接发送，手动触发onSubmit
    if (!userInputValue.trim() && fileList.length) {
      handleSendMessage();
    }
  };

  const handleKeyDown = async e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      if (!userInputValue.trim() && fileList.length && senderClickable) {
        setSenderClickable(false);
        handleSendMessage();
      } else {
        e.preventDefault();
      }
    }
  };

  const handleOnChange = value => {
    if (value.length > MAX_INPUT) {
      setUserInputValue(value.slice(0, MAX_INPUT));
      message.error(t('chat:maxWords', { max: MAX_INPUT }));
    } else {
      setUserInputValue(value);
    }
  };

  const renderDefaultSender = () => {
    return (
      <div data-testid="scams-report-chat-sender" style={{ padding: 16 }}>
        <Sender
          ref={senderRef}
          data-testid="scams-report-chat-input"
          header={headerNode}
          loading={isCurrentLoading}
          placeholder={t('chat:senderPlaceholder')}
          value={userInputValue}
          onKeyDown={handleKeyDown}
          onChange={handleOnChange}
          onSubmit={handleSendMessage}
          onCancel={onCancel}
          onPasteFile={file => {
            attachmentsRef.current?.upload(file);
          }}
          style={{
            borderRadius: '1.5rem',
            padding: '.1rem',
          }}
          autoSize={{ minRows: 2, maxRows: 6 }}
          actions={false}
          footer={({ components }) => {
            const { SendButton, LoadingButton } = components;
            return (
              <Flex justify="space-between" align="center">
                <Flex gap="small" align="center">
                  <Popover content={t('chat:tips')}>
                    <Upload {...sharedAttachmentProps} showUploadList={false}>
                      <Button type="text" icon={<LinkOutlined />} />
                    </Upload>
                  </Popover>
                </Flex>
                <Flex align="center">
                  <CountWord
                    current={userInputValue?.length}
                    max={MAX_INPUT}
                    currentLimiti={current}
                    total={total}
                    isloggin={isLoggedIn}
                  />
                  {isCurrentLoading ? (
                    <LoadingButton type="default" />
                  ) : (
                    <SendButton
                      data-testid="scams-report-send-button"
                      type="primary"
                      disabled={!senderAvailable() || !senderClickable}
                      onClick={() => {
                        setSenderClickable(false);
                        onlySendImage();
                      }}
                    />
                  )}
                </Flex>
              </Flex>
            );
          }}
        />
        {/* Sensitive information prompt */}
        <Flex align="center" gap="6px" style={{ paddingTop: '1%', paddingLeft: '3%' }}>
          <WarningOutlined />
          <span style={{ color: 'gray' }}>{t('chat:SensitiveInformationPrompt')}</span>
        </Flex>
      </div>
    );
  };

  const gotoReport = () => {
    const target = getCurrentConversion();
    if (target) {
      navigate('/report/' + target.reportId);
      dispatch(setReportId(target.reportId));
    }
  };

  const goHome = () => {
    setIsLeavingConversation(true);
    navigate('/');
  };

  const { runAsync: updateConversationIsFeedback } = useRequest(updateConversationAPI, {
    manual: true,
  });

  const showFeedbackModal = () => {
    dispatch(setIsShowFeedback(true));
    dispatch(setIsShowChatFeedbackFooter(true));
    updateConversationIsFeedback({
      externalRef: externalRef,
      isFeedback: 2,
    });
  };

  const currentConversion = getCurrentConversion();
  let hasRisk = '';
  if (Array.isArray(messages) && messages.length > 0) {
    messages.forEach(subItem => {
      if (subItem.role === 'assistant') {
        if (Array.isArray(subItem.content)) {
          subItem.content.forEach(item => {
            if (item.type === 'result' && item.content.type === 'risk') {
              hasRisk = item.content.content;
            }
          });
        } else if (Array.isArray(subItem.contents)) {
          subItem.contents.forEach(item => {
            if (item.type === 'result' && item.content.type === 'risk') {
              hasRisk = item.content.content;
            }
          });
        } else {
          hasRisk = messages.find(
            item => item.content?.type === 'risk' || item.content?.type === 'reevaluate'
          );
        }
      }
    });
  }
  useEffect(() => {
    if (currentConversion?.reportId || hasRisk) {
      setIsDraggingShown(false);
    } else {
      setIsDraggingShown(true);
    }
  }, [currentConversion, hasRisk]);

  // Ctrl+C img / Ctrl+V img
  useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste]);

  const renderChatSender = () => {
    if (!externalRef || isLeavingConversation) {
      return renderDefaultSender();
    }

    let type = '';
    if (currentConversion && currentConversion.reportId) {
      type = 'reported';
    } else if (hasRisk) {
      type = 'risk';
    }
    switch (type) {
      case 'reported':
        if (isMobile)
          return (
            <ChatFooterMobile content={t('chat:footer.reportedMobile')} onClick={gotoReport} />
          );
        return (
          <ChatFooter
            content={t('chat:footer.reportedContent')}
            btnText={t('chat:footer.reportedBtn')}
            onClick={gotoReport}
          />
        );
      case 'risk':
        if (isFeedbackStatusLoading) {
          return renderDefaultSender();
        }
        if (isShowChatFeedbackFooter) {
          if (isMobile) return <ChatFooterMobile content="New Chat" onClick={goHome} />;
          return (
            <ChatFooter
              content={t('chat:footer.riskContent')}
              btnText={t('chat:footer.riskBtn')}
              onClick={goHome}
            />
          );
        } else {
          return <ChatFeedback externalRef={externalRef} onClick={showFeedbackModal} />;
        }
      default:
        return renderDefaultSender();
    }
  };

  const renderContent = () => {
    if (isFromHome) {
      return (
        <div style={{ padding: 16 }}>
          <HomeSenderView />
        </div>
      );
    }

    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }} ref={chatListRef}>
        <Flex
          gap="middle"
          vertical
          style={{ width: isMobile ? '100%' : '65%', maxWidth: '1200px', margin: '0 auto' }}
        >
          {messages.map(item => {
            if (Array.isArray(item.content) && item.content.length > 0) {
              return item.content.map(subItem => {
                if (subItem.type === 'result' && !allowedResultIds.has(subItem.id)) return null;
                return (
                  <CustomBubbleRender
                    key={`${item.id}-${subItem.id}`}
                    message={subItem}
                    role={item.role}
                    createdAt={item.createdAt}
                    typing={item.typing}
                    onSubmit={handleSendMessage}
                    isReevaluate={item.isReevaluate}
                    apiError={item.id === retryErrorMessageId}
                    cotComplete={cotComplete}
                    setCotRenderComplete={setCotRenderComplete}
                  />
                );
              });
            } else if (Array.isArray(item.contents) && item.contents.length > 0) {
              return item.contents.map((subItem, index) => (
                <CustomBubbleRender
                  key={`${item.messageId || item.externalRef || 'history'}-${index}`}
                  message={subItem}
                  role={item.role}
                  createdAt={item.createdAt}
                  typing={true}
                  onSubmit={handleSendMessage}
                  isReevaluate={item.isReevaluate}
                  apiError={false}
                  historyData={true}
                />
              ));
            } else {
              if (item.content?.type === 'reevaluate') {
                if (userProfile.userStatus === 'Login User') {
                  return (
                    <CustomBubbleRender
                      key={item.externalRef || item.id}
                      message={item.content}
                      role={item.role}
                      createdAt={item.createdAt}
                      typing={item.typing}
                      onSubmit={handleSendMessage}
                      isReevaluate={item.isReevaluate}
                      apiError={item.id === retryErrorMessageId}
                    />
                  );
                }
              } else {
                return (
                  <CustomBubbleRender
                    key={item.externalRef || item.id}
                    message={item.content}
                    role={item.role}
                    createdAt={item.createdAt}
                    typing={item.typing}
                    onSubmit={handleSendMessage}
                    isReevaluate={item.isReevaluate}
                    apiError={item.id === retryErrorMessageId}
                  />
                );
              }
            }
          })}

          {isCurrentLoading && (
            <Bubble loading={true} content={t('chat:typing')} role="assistant" placement="start" />
          )}
          <div style={{ minHeight: '10vh' }} />
        </Flex>
      </div>
    );
  };

  return (
    <div
      className={themeMode}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      {isDragging && isDraggingShown && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            zIndex: 1000,
            transition: 'opacity 0.3s ease',
          }}
        >
          <CloudUploadOutlined style={{ fontSize: 48, color: '#16796f' }} />
          <p style={{ fontSize: 18, color: '#16796f', marginTop: 10 }}>{t('chat:dragDrop')}</p>
        </div>
      )}
      <GuidePage />

      {/* 消息展示区域 */}
      {renderContent()}
      {isShowFeedback && <FeedbackModal typeModal="chat" />}

      {/* 消息输入区域 */}
      <div
        style={{
          position: 'relative',
          bottom: isFromHome ? 0 : '3%',
          width: isMobile ? '100%' : '65%',
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center',
        }}
      >
        {!isFromHome && scrollButton && (
          <Button
            type="primary"
            onClick={scrollToBottom}
            style={{
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              padding: 0,
            }}
            icon={<AntImage src={iconSrc} width={22} preview={false}></AntImage>}
          />
        )}

        {renderChatSender()}
      </div>
      <LoginModal
        isLoginModalOpen={isLoginModalOpen}
        handleLoginModalClose={handleLoginModalClose}
        handleLogin={handleLogin}
      />
    </div>
  );
};
export default NewChat;
